import { WordData } from '@shared/types/words';
import Button from 'react-bootstrap/Button';
import { useSpring, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { formatMilliseconds } from '@app/utils/formaters';
import './SwipeableWordItem.scss';
import { useRef } from 'react';

interface SwipeableWordItemProps {
  wordData: WordData;
  onDelete?: (word: WordData) => void;
  onSave?: (word: WordData) => void;
}

const SwipeableWordItem = ({ wordData, onDelete, onSave }: SwipeableWordItemProps) => {
  const actionsElRef = useRef<HTMLDivElement>(null);
  const swipeDirectionRef = useRef<number>(0);
  const [{ x }, springsApi] = useSpring(() => ({ x: 0 }));
  
  const bindGesture = useDrag(
    ({ movement: [mx], direction: [xDir], active, target, cancel }) => {
      if (!(target as HTMLElement).closest('.swipe-handle')) {
        cancel();
        springsApi.start({ x: 0 });
        return;
      }

      const actionsWidth = actionsElRef.current?.offsetWidth || 0;

      if (!active) {
        springsApi.start({ x: swipeDirectionRef.current < 0 ? -actionsWidth : 0 });
        swipeDirectionRef.current = 0;
        return;
      }

      if (mx > 0) {
        springsApi.start({ x: 0 });
        return;
      }

      swipeDirectionRef.current = xDir;

      const newX = Math.abs(mx) > actionsWidth ? -actionsWidth : mx;
      springsApi.start({ x: newX });
    }
  );

  const formatTs = (seconds?: number) => {
    if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) {
      return '';
    }
    const base = formatMilliseconds(seconds);
    if (!base) {
      return '';
    }
    if (base.startsWith('00:')) {
      const mmss = base.slice(3);
      const [mm, ss] = mmss.split(':');
      return `${Number(mm)}:${ss}`;
    }
    return base;
  };

  return (
    <li className="swipeable-item">
      <animated.div
        {...bindGesture()}
        style={{
          x,
          position: 'relative',
          zIndex: 2,
          background: 'var(--bs-light)',
          // touchAction: 'none'
        }}
      >
        <div className="word-content">
          <h3>
            <span className="word">{wordData.word}</span>{' '}
            [<span className="transcription">{wordData.transcription}</span>]
          </h3>
          <p className="definition">{wordData.definition}</p>
          <div className="examples-title">Examples</div>
          <ul className="example-phrases">
            {wordData.example_phrases.map((phrase, idx) => (
              <li key={idx}>
                <div className="phrase-row">
                  <span className="phrase">{phrase.phrase}</span>
                  {typeof phrase.ts === 'number' && (
                    <span className="phrase-ts">{formatTs(phrase.ts)}</span>
                  )}
                </div>
                <p className="explanation">{phrase.explanation}</p>
              </li>
            ))}
          </ul>
        </div>
        <div 
          className="swipe-handle"
          title="Swipe left to remove"
        >
          <i className="ic ic-three-dots-vertical" />
        </div>
      </animated.div>
      <div className="actions" ref={actionsElRef} onClick={() => (springsApi.start({ x: 0 }))}>
        {typeof onDelete === 'function' && (<Button 
          variant="link" 
          className="action-button action-button-delete" 
          onClick={() => onDelete(wordData)}
          style={{ textDecoration: 'none' }}
        >
          <i className="ic ic-delete" />
        </Button>)}
        {typeof onSave === 'function' && (<Button 
          variant="link" 
          className="action-button" 
          onClick={() => onSave(wordData)}
          style={{ textDecoration: 'none' }}
          disabled={wordData.saved}
        >
          <i className={`ic ${wordData.saved ? 'ic-bookmark-star' : 'ic-bookmark-plus'}`} />
        </Button>)}
      </div>
    </li>
  );
};

export default SwipeableWordItem; 
// ic-bookmark-plus
